/**
 * favoriteCatalog.js — PC 사이드바 즐겨찾기 재료 (2026-10-22)
 *
 * 사용자: "PC버전에도 즐겨찾기 기능 추가해줘 즐겨찾기하면 내정보 아래 고정으로 떴으면 좋겠어"
 *   휴대폰 바로가기(mobile_shortcuts)와 따로, 최대 5개, 메뉴에 마우스를 올리면 ☆, 회사·통합 메뉴도 넣을 수 있다(사용자 선택).
 *
 * 즐겨찾기 한 칸 = { to, label, company, companyName, tenant } — 개인 설정 user_preferences 'desktop_favorites'.
 *   company·tenant = 별을 누를 때의 작업 대상. 같은 주소라도 작업 대상마다 다른 화면이라서다
 *   (운영자가 고객 테넌트를 고르면 /admin/contracts 가 그 테넌트 계약이다). 회사와 무관한 화면(내 정보·시스템)은 비운다.
 *
 * 보이는 조건 = 지금 그 사람이 사이드바에서 열 수 있는 메뉴(fail-closed). 사이드바와 같은 함수(composeAdminMenus)로
 *   작업 대상마다 메뉴를 만들어 대 본다 — 열쇠가 빠지면 즐겨찾기에서도 숨는다.
 *   운영자는 다른 테넌트의 메뉴를 미리 알 수 없어(그 테넌트를 골라야 권한을 받아 온다) 지금 고른 곳 밖의 칸은
 *   테넌트가 살아 있고 주소가 앱에 있는지만 본다 — 운영자는 열쇠가 전부다.
 */
import { Layers } from 'lucide-react';
import { MENU_GROUPS, ADMIN_ONLY_MENUS } from './menuConfig';
import { COMPANY_MENU_FEATURES } from './companyFeatures';
import { GLOBAL_GROUP_NAMES } from './companyMenuSet';
import { buildStayPaths } from './companySwitchTarget';
import { composeAdminMenus } from './menuComposition';
import { filterAdminOnlyMenus } from './sidebarFilters';
import { INTEGRATED_HOME_PATH, INTEGRATED_MANAGE_PATH, isIntegratedPath, groupIntegratedMenus } from './integratedScope';
import { sidebarIntegratedMenus } from '../attendancehub/integratedAttendanceTabs';

export const FAVORITE_MAX = 5;
export const FAVORITE_KEY = 'desktop_favorites';

const upper = (v) => String(v || '').trim().toUpperCase();
// 작업 대상을 바꿔도 그대로 두는 화면(시스템 묶음·통합 메뉴 관리 등) — 회사 전환 규칙과 같은 표
const STAY_PATHS = buildStayPaths(MENU_GROUPS, GLOBAL_GROUP_NAMES, ADMIN_ONLY_MENUS);
const NO_PLACE = { company: null, companyName: null, tenant: null };

/** 같은 즐겨찾기인지 가르는 열쇠 — 주소 + 작업 대상. */
export function favKey(f) {
  return `${f?.to || ''}|${upper(f?.company)}|${upper(f?.tenant)}`;
}

/** 이 주소가 지금 작업 대상에서 어디에 속하나 — 별을 누를 때 함께 저장한다. */
export function placeOf(to, scope) {
  const p = String(to || '');
  if (p.startsWith('/my/') || STAY_PATHS.has(p)) return NO_PLACE;
  const tenant = scope?.isOperator ? (scope.selectedTenant?.code || null) : null;
  if (isIntegratedPath(p)) return { ...NO_PLACE, tenant };
  const c = scope?.company || null;
  return { company: c?.code || null, companyName: c?.name || null, tenant };
}

/** 서버에 보낼 모양(허용된 이름만). */
export function toSaved(f) {
  return {
    to: f.to,
    label: String(f.label || '').slice(0, 60),
    company: f.company || null,
    companyName: f.companyName ? String(f.companyName).slice(0, 60) : null,
    tenant: f.tenant || null,
  };
}

/** 서버에서 받은 값 정리 — 모양이 틀린 칸·중복은 버리고 5개까지. */
export function normalizeFavorites(value) {
  const out = [];
  const seen = new Set();
  for (const f of Array.isArray(value) ? value : []) {
    if (!f || typeof f.to !== 'string' || !f.to.startsWith('/') || typeof f.label !== 'string') continue;
    const k = favKey(f);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(toSaved(f));
  }
  return out.slice(0, FAVORITE_MAX);
}

function addItem(map, item, place) {
  const entry = { to: item.to, label: item.label, Icon: item.Icon || Layers, codes: item.codes || null, ...place };
  const k = favKey(entry);
  if (!map.has(k)) map.set(k, entry);
}

/**
 * 지금 열 수 있는 메뉴 전부 — 열쇠(favKey) → 칸. 사이드바와 같은 재료로 작업 대상마다 만든다.
 * 운영자는 지금 고른 회사만(다른 테넌트·회사의 권한은 그곳을 골라야 서버에서 온다).
 */
export function buildFavoriteCatalog({ cats, permissions, permCodes, isAdmin, scope, myMenus = [], fallbackIntegrated = [] }) {
  const map = new Map();
  for (const m of myMenus) addItem(map, m, NO_PLACE);
  const companies = isAdmin ? [scope?.company || null] : (scope?.companies?.length ? scope.companies : [null]);
  const adminOnly = filterAdminOnlyMenus(ADMIN_ONLY_MENUS, isAdmin);
  let menuGroups = [];
  for (const c of companies) {
    const here = { ...(scope || {}), company: c };
    const r = composeAdminMenus({ cats, permissions, permCodes, isAdmin, scope: here });
    menuGroups = r.menuGroups;
    for (const g of r.visibleGroups) {
      if (g.group === '현황') continue; // 사이드바도 그리지 않는 묶음(상황판은 위쪽 고정)
      for (const it of [...g.items, ...(g.group === '시스템' ? adminOnly : [])]) addItem(map, it, placeOf(it.to, here));
    }
  }
  if (scope?.canUseIntegrated) {
    addItem(map, { to: INTEGRATED_HOME_PATH, label: '통합 관리 홈', Icon: Layers }, placeOf(INTEGRATED_HOME_PATH, scope));
    for (const g of groupIntegratedMenus(sidebarIntegratedMenus(scope.integratedMenus), menuGroups, cats?.order)) {
      for (const it of g.items) addItem(map, { ...it, codes: it.menu.company_codes }, placeOf(it.to, scope));
    }
    if (scope.canManageIntegrated) {
      addItem(map, { to: INTEGRATED_MANAGE_PATH, label: '통합 메뉴 관리', Icon: Layers }, placeOf(INTEGRATED_MANAGE_PATH, scope));
    }
  }
  for (const m of fallbackIntegrated) {
    const to = `${INTEGRATED_HOME_PATH}/${m.id}`;
    addItem(map, { to, label: m.name, Icon: Layers, codes: m.company_codes }, placeOf(to, scope));
  }
  return map;
}

const STATIC_ITEMS = [...MENU_GROUPS.flatMap((g) => g.items || []), ...ADMIN_ONLY_MENUS];

/** 주소가 앱에 있는 메뉴인가 — 있으면 아이콘, 없으면 null (운영자의 다른 테넌트 칸용). */
export function staticMenuIcon(to) {
  const p = String(to || '');
  const hit = STATIC_ITEMS.find((m) => m.to === p);
  if (hit) return hit.Icon || Layers;
  const seg = p.match(/^\/admin\/ext\/[^/]+\/(.+)$/)?.[1];
  if (seg) return COMPANY_MENU_FEATURES.find((f) => f.path === seg)?.Icon || null;
  return /^\/admin\/integrated(\/\d+)?$/.test(p) ? Layers : null;
}

/** 저장된 즐겨찾기 → 그릴 칸. 열 수 없는 칸은 뺀다(fail-closed). 이름·아이콘은 지금 메뉴 것을 쓴다. */
export function resolveFavorites(saved, catalog, { isAdmin, scope }) {
  const tenants = new Set((scope?.tenants || []).map((t) => upper(t.code)));
  const curTenant = upper(scope?.selectedTenant?.code);
  const curCompany = upper(scope?.company?.code);
  const out = [];
  for (const f of saved || []) {
    const hit = catalog.get(favKey(f));
    if (hit) {
      out.push({ ...f, label: hit.label, Icon: hit.Icon, codes: hit.codes, companyName: hit.companyName || f.companyName });
      continue;
    }
    if (!isAdmin) continue;
    // 운영자: 지금 고른 곳의 칸인데 메뉴에 없으면 숨긴다. 다른 테넌트·회사의 칸은 테넌트가 살아 있고 주소가 앱에 있을 때만.
    const here = (!f.tenant || upper(f.tenant) === curTenant) && (!f.company || upper(f.company) === curCompany);
    if (here || (f.tenant && !tenants.has(upper(f.tenant)))) continue;
    const Icon = staticMenuIcon(f.to);
    if (Icon) out.push({ ...f, Icon, codes: null });
  }
  return out;
}
